import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  createInverter,
  deleteInverter,
  getAllInverters,
  getInverter,
  GetInverterResponse,
  InverterRequest,
  InverterToDeviceWithInverterWithErrors,
  updateInverter,
} from '@app/api/inverter.api'

interface InverterState {
  data: GetInverterResponse[]
  dataAll: InverterToDeviceWithInverterWithErrors[]
  isLoading: boolean
  error: string | undefined
  isLoadingInvertersError: boolean
  selectedInverter?: GetInverterResponse
}

const initialState: InverterState = {
  isLoading: false,
  error: undefined,
  data: [],
  dataAll: [],
  selectedInverter: undefined,
  isLoadingInvertersError: false,
}

export interface InverterUpdateRequest {
  id: string
  data: InverterRequest
}

export const getAllInvertersData = createAsyncThunk<GetInverterResponse[]>(
  'inverter/getAllInverter',
  (_, { rejectWithValue }) =>
    getAllInverters()
      .then((res) => res)
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const createInverterData = createAsyncThunk<GetInverterResponse, InverterRequest>(
  'inverter/createInverter',
  (createInverterPayload, { rejectWithValue }) =>
    createInverter(createInverterPayload)
      .then((res) => {
        return res
      })
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const updateInverterData = createAsyncThunk<GetInverterResponse, InverterUpdateRequest>(
  'inverter/updateInverter',
  (updateInverterPayload, { rejectWithValue }) =>
    updateInverter(updateInverterPayload.id, updateInverterPayload.data)
      .then((res) => {
        return res
      })
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const deleteInverterData = createAsyncThunk<GetInverterResponse, string>(
  'inverter/deleteInverter',
  (id, { rejectWithValue }) =>
    deleteInverter(id)
      .then((res) => {
        return res
      })
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const getInverterById = createAsyncThunk<GetInverterResponse, string>(
  'inverter/getInverterById',
  (id, { rejectWithValue }) =>
    getInverter(id)
      .then((res) => res)
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const inverterSlice = createSlice({
  name: 'inverter',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllInvertersData.pending, (state) => {
        state.error = undefined
        state.isLoading = true
      })
      .addCase(getAllInvertersData.fulfilled, (state, action: PayloadAction<GetInverterResponse[]>) => {
        state.isLoading = false
        state.data = action.payload
      })
      .addCase(getAllInvertersData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })

      .addCase(createInverterData.pending, (state) => {
        state.error = undefined
        state.isLoading = true
      })
      .addCase(createInverterData.fulfilled, (state, action: PayloadAction<GetInverterResponse>) => {
        state.isLoading = false
        state.data.push(action.payload)
      })
      .addCase(createInverterData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(updateInverterData.pending, (state) => {
        state.error = undefined
        state.isLoading = true
      })
      .addCase(updateInverterData.fulfilled, (state, action: PayloadAction<GetInverterResponse>) => {
        state.isLoading = false
        const index = state.data.findIndex((item) => item.id === action.payload.id)
        if (index !== -1) {
          state.data[index] = action.payload
        }
      })
      .addCase(updateInverterData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(deleteInverterData.pending, (state) => {
        state.error = undefined
        state.isLoading = true
      })
      .addCase(deleteInverterData.fulfilled, (state, action: PayloadAction<GetInverterResponse>) => {
        state.isLoading = false
        state.data = state.data.filter((inverter) => inverter.id !== action.payload.id)
      })
      .addCase(deleteInverterData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(getInverterById.pending, (state) => {
        state.error = undefined
        state.isLoading = true
      })
      .addCase(getInverterById.fulfilled, (state, action: PayloadAction<GetInverterResponse>) => {
        state.isLoading = false
        state.selectedInverter = action.payload
      })
      .addCase(getInverterById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export default inverterSlice.reducer
